/* eslint-disable @typescript-eslint/prefer-optional-chain */
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { InjectorService, StateManager } from '@tencent/workbench';
import { ToolbarType } from '@src/js/constant';
import { SlideSizeType } from '@src/slide/domain/ui-model/element-modeles/property/slide-size/slide-size.interface';
import _omit from 'lodash/omit';
import {
    TDWModuleType,
    TDWOperationType,
    TDWVer4Type,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { tdwDrawerReport } from '@framework/workbench/drawer/report';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { IUserPriviledgeService } from '@src/slide/application/service/user-priviledge-service/user-priviledge-service.interface';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { browser } from '@src/utils/agent';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IMenuManager } from '@src/slide/application/service/menu-manager/menu-manager.interface';
import { IBaseToolbarFeature } from './base-toolbar-interface';
import {
    IColorPickerManager,
} from '@src/slide/application/service/color-picker-manager/color-picker-manager.interface';
import { BaseFeatureState } from './base-feature-state';
import { ISelectedAnimManager } from '@src/slide/application/service/selection/selected-anim-manager.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { ESelectType } from './base-drawer-interface';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { wrapIdForBig } from '../../util/wrap-id-util';

// 母版禁用 workbench 中的能力
const DISABLED_ID_IN_MASTER = [
    ActionType.INSERT_IFRAME_ITEM,
    ActionType.UPLOAD_LOCAL_FILE,
    ActionType.SEARCH_REPLACE,
    ActionType.INSERT_ANIMATION,
    ActionType.INTELLIGENT_TOOL,
    ActionType.INSERT_WITH_LATEX,
    wrapIdForBig(ActionType.INSERT_WITH_LATEX),
    ActionType.INSERT_TENCENT_DOC_ID,
];

export default class BaseToolbarFeature extends BaseFeatureState implements IBaseToolbarFeature {
    public id = ''; // 子类需要获取自己的id
    public config;

    /**
     * menuManager的toolbar基础功能回调，个性化功能可在继承后覆盖
     * @param obj
     * @param option
     */
    public callback(obj?: any, option?: any) {
        this.getToolbarMenu()?.callback(MenuType.TOOLBAR, this.id, obj, option);
        this.twdReport(obj);
    }

    /**
     * menuManager的toolbar基础功能回调，个性化功能可在继承后覆盖
     * @param obj
     * @param option
     */
    public callbackByActionType(actionType, obj?: any, option?: any) {
        this.getToolbarMenu()?.callback(MenuType.TOOLBAR, actionType, obj, option);
        this.twdReport(obj || actionType);
    }

    /**
     * 针对侧边栏的回调。侧边栏的部分功能和工具栏相同，但上报数据不同
     * @param obj
     * @param option
     */
    public drawerCallback(obj?: any, option?: any) {
        this.getToolbarMenu()?.callback(MenuType.TOOLBAR, this.id, obj, option);
        tdwDrawerReport(this.id, obj ? { ver5: obj } : {});
    }

    /**
     * 针对侧边栏的回调。侧边栏的部分功能和工具栏相同，但上报数据不同
     * @param obj
     * @param option
     */
    public drawerCallbackByActionType(actionType, obj?: any, option?: any) {
        this.getToolbarMenu()?.callback(MenuType.TOOLBAR, actionType, obj, option);
        const options: {ver5?: any} = {};
        if (actionType) {
            options.ver5 = actionType;
        } else if (obj) {
            options.ver5 = obj;
        }
        tdwDrawerReport(this.id, options);
    }


    /**
     * 获取当前按键的值
     */
    public getItemValue(id?) {
        const values = this.getToolbarMenu()?.values;
        return values?.[id || this.id] || '';
    }

    /**
     * 获取带当前值的toolbar.item列表
     */
    public getItems() {
        const toolbarMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();
        const isDoubleToolbar = toolbarMode === ToolbarType.DOUBLE_TOOLBAR;
        let items;
        if (isDoubleToolbar) {
            items = this.getDoubleItems()?.items;
        } else {
            items = this.getSingleItems()?.items;
        }
        return items;
    }

    /**
     * 获取单行items
     */
    public getSingleItems() {
        const toolbarData = this.getToolbarMenu();
        const disableItems = this.getMenuManager()?.disableItemIds();

        const values = toolbarData?.values;
        const disableIDs = {};
        disableItems?.map((item) => {
            disableIDs[item.id] = true;
        });

        toolbarData?.items?.forEach((item) => {
            const value = values?.[item.id];
            if (value !== null && value !== undefined) {
                item.value = value;
            }

            if (disableIDs[item.id] && !item.enable) {
                // 通过显式设置Item的enable=true，可以覆盖固定的disable列表。
                item.disabled = true;
            } else {
                item.disabled = false;
            }

            if (item.id === ActionType.SLIDE_SIZE) {
                const { slideSizeType } = this.getUiDataService().presentation.props;
                if (slideSizeType === SlideSizeType.SLIDE_SIZE_4_3) {
                    item.value = ActionType.SLIDE_STANDARD;
                } else if (slideSizeType === SlideSizeType.SLIDE_SIZE_16_9) {
                    item.value = ActionType.SLIDE_WIDE;
                } else {
                    item.value = ActionType.SLIDE_CUSTOM;
                }
            }
        });

        return toolbarData;
    }

    /**
     * 获取双行items
     */
    public getDoubleItems() {
        const toolbarData = this.getToolbarMenu();
        const disableItems = this.getMenuManager()?.disableItemIds();

        const values = toolbarData?.values;
        const disableIDs = {};
        disableItems?.forEach((item) => {
            disableIDs[item.id] = true;
        });

        toolbarData?.items?.forEach((outterItem) => {
            // item有三种：1）divider 2)big 3)top + bottom
            const processItem = (item) => {
                const value = values?.[item.id];
                if (value !== null && value !== undefined) {
                    item.value = value;
                }

                if (disableIDs[item.id] && !item.enable) {
                    item.disabled = true;
                } else {
                    item.disabled = false;
                }
            };
            if (outterItem.big) {
                outterItem.big.forEach((item) => {
                    processItem(item);
                });
            } else if (outterItem.top) {
                outterItem.top.forEach((item) => {
                    processItem(item);
                });
                outterItem.bottom.forEach((item) => {
                    processItem(item);
                });
            }
        });

        return toolbarData;
    }

    public updateDropdownVisible(visible: boolean, id) {
        if (visible) {
            this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarMenuShown = id;
            return;
        }
        if (this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarMenuShown === id) {
            this.getAppService().viewableServiceLayer.toolbarStateManager.isToolbarMenuShown = false;
        }
    }

    public getBaseTwdReportConfig() {
        const ver4Type = TDWVer4Type.TOOLBAR_NORMAL_DISPLAY;
        const toolbarMode = this.getAppService().viewableServiceLayer.toolbarStateManager.getToolbarMode();

        // todo: 等wb支持识别更多面板后，需修改，用于区分当前的click是否来自更多面板
        // if (props.isMoreToolbar) {
        //     this.ver4Type = TDWVer4Type.TOOLBAR_WRAP_DISPLAY;
        // }

        // todo: 等wb拿到tab激活态
        // const state = InjectorService.get(StateManager);
        return {
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.PC_TOOLBAR,
            ver3: toolbarMode,
            ver4: ver4Type,
        };
    }

    /**
     * 上报，等wb支持中间件取值后，迁到中间件统一上报
     * @private value 上报值
     * @private clickFrom 请求来源
     */
    public twdReport(value?, clickFrom?, config?) {
        const reportObj = {
            ...this.getBaseTwdReportConfig(),
        };

        const twdConfig = config?.twdConfig || this.config?.twdConfig;
        // 获取每一个种类自己的上报配置，不配置不上报
        if (twdConfig) {
            // 一个按钮可能存在多个产品需求的上报
            let configList;
            if (Array.isArray(twdConfig)) {
                configList = twdConfig;
            } else {
                configList = [twdConfig];
            }
            configList.forEach((config) => {
                const reportConfig = {
                    ...config,
                };
                const { ver5, handleFunction, notReportVer5 } = reportConfig;

                // 某些按钮不需要上报ver5
                if (!ver5 && !notReportVer5) {
                    const ver5Value = value || this.getItemValue();

                    // 自定义的上报参数处理，搬运原有逻辑
                    if (handleFunction) {
                        const handleObject = handleFunction(value, clickFrom);
                        Object.assign(reportConfig, handleObject);
                    } else {
                        reportConfig.ver5 = ver5Value;
                    }
                }
                Object.assign(reportObj, reportConfig);
                const omitReportObj = _omit(reportObj, 'notReportVer5', 'handleFunction');
                tdw(omitReportObj);
            });
        }
    }

    public shouldDisableInMaster(id: string): boolean {
        const isMasterEditType = this.getMasterLayoutEditService()?.isMasterEditType;
        if (DISABLED_ID_IN_MASTER.includes(id)) {
            return isMasterEditType ?? false;
        }
        return false;
    }

    public getAppService(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IPcEditorAppServices;
    }

    public getColorPickerManager(): IColorPickerManager | undefined {
        return this.getAppService().editableServiceLayer?.colorPickerManager;
    }

    public getUiDataService(): IUiDataService {
        return this.getAppService().getUiDataService();
    }

    public getMenuManager(): IMenuManager | undefined {
        return this.getAppService().editableServiceLayer?.menuManager;
    }

    public getUserPriviledgeService(): IUserPriviledgeService {
        return this.getAppService().viewableServiceLayer.userPriviledgeService;
    }

    public getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
        if (browser.isMobile || !this.getUserPriviledgeService().isEditable()) {
            return undefined;
        }
        return (this.getAppService() as IPcEditorAppServices).editableServiceLayer?.masterLayoutEditService;
    }

    /**
     * 获取当前选中元素
     */
    public getSelectModel() {
        return this.getAppService().viewableServiceLayer.selectionService.getSelectedModel();
    }

    public getSelectedAnimManager(): ISelectedAnimManager | undefined {
        return this.getAppService()?.editableServiceLayer?.selectedAnims;
    }

    public getSelectionService(): ISelectionService {
        return this.getAppService()?.viewableServiceLayer.selectionService;
    }

    public checkSelectType = (): ESelectType => {
        const selectedSlideElements = this.getSelectionService().selectedElements
            .selectedArrayForType(SelectedEleMgrType.SLIDE);
        const groupSelectedUIModelArr = this.getSelectionService().selectedElements
            .selectedArrayForType(SelectedEleMgrType.GROUP);
        if (selectedSlideElements.length > 1) return ESelectType.MULTY_SLIDE_OR_GROUP_HAS_CHILDREN;
        if (selectedSlideElements.length < 1) return ESelectType.NO_SLIDE_OR_OTHER;
        const element = selectedSlideElements[0] as IElementBaseModel;
        if ([UIModelType.SHAPE, UIModelType.IMAGE, UIModelType.CONNECTOR].includes(element.type)) {
            return ESelectType.SHAPE_OR_IMAGE_OR_CONNECTOR;
        }
        if (element.type === UIModelType.GROUP) {
            return groupSelectedUIModelArr.length > 1
                ? ESelectType.MULTY_SLIDE_OR_GROUP_HAS_CHILDREN
                : ESelectType.GROUP_NO_CHILD;
        }
        return ESelectType.NO_SLIDE_OR_OTHER;
    };

    protected getToolbarMenu() {
        return this.getMenuManager()?.toolbarMenu();
    }
}
